/** Read-only grid of a booking's tickets.
 *
 * Tickets that share a `ticket_id` are grouped under a tree node named by their
 * `label` (MUI `treeData`); unique tickets stay as flat top-level rows. Clicking
 * a ticket row opens a drawer with every property. Less-important fields
 * (value/remote_value/currency/file) are exposed as columns but hidden by
 * default; `value` in particular is a hidden-by-default column.
 *
 * A sub-booking owns no tickets: it displays its main booking's, and those rows
 * arrive with `inherited: true` (the Source column names the owner, and the row is
 * styled as context rather than own data). They are present exactly when the
 * operator has `inherit_tickets_from_main_booking` on, so this grid answers "what
 * does this guest see in the app?" the same way the app does.
 */
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Drawer, IconButton, Link, Typography } from '@mui/material';
import {
  DataGridPro,
  useGridApiContext,
  type GridColDef,
  type GridGroupingColDefOverride,
  type GridRenderCellParams,
  type GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingTicket } from '@/api/bookings';
import { Field, FieldGrid, LinkField } from '@/components/ReadView';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';

import { EmptyLine, formatMoney, READONLY_GRID_PROPS, READONLY_GRID_SX, Ts } from './common';

/** A copyable cell value; the trigger stops propagation so hovering/clicking
 *  it never opens the row's drawer by accident. */
function Copyable({ value, label }: { value: string | null; label: string }) {
  if (!value) return <>—</>;
  return <ValueActions actions={[act.copy(value, label)]}>{value}</ValueActions>;
}

type TicketRow = BookingTicket & { path: string[] };

/** Group tickets sharing a ticket_id (label as the group name); unique tickets
 *  stay flat. Returns rows with a tree path + the group→label map. */
function buildRows(tickets: BookingTicket[]): { rows: TicketRow[]; labelByGroup: Map<string, string> } {
  const counts = new Map<string, number>();
  for (const t of tickets) if (t.ticket_id) counts.set(t.ticket_id, (counts.get(t.ticket_id) ?? 0) + 1);
  const labelByGroup = new Map<string, string>();
  const rows = tickets.map((t) => {
    const grouped = t.ticket_id != null && (counts.get(t.ticket_id) ?? 0) > 1;
    if (grouped) {
      const key = `g:${t.ticket_id}`;
      if (!labelByGroup.has(key)) labelByGroup.set(key, t.label || t.ticket_id || key);
      return { ...t, path: [key, String(t.id)] };
    }
    return { ...t, path: [String(t.id)] };
  });
  return { rows, labelByGroup };
}

function GroupingCell({
  params,
  labelByGroup,
}: {
  params: GridRenderCellParams<TicketRow>;
  labelByGroup: Map<string, string>;
}) {
  const apiRef = useGridApiContext();
  const { id, row, rowNode } = params;
  if (rowNode.type === 'group') {
    const expanded = rowNode.childrenExpanded;
    const label = labelByGroup.get(String(rowNode.groupingKey)) ?? String(rowNode.groupingKey);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          onClick={(e) => {
            e.stopPropagation();
            apiRef.current.setRowChildrenExpansion(id, !expanded);
          }}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
      </Box>
    );
  }
  const depth = rowNode.depth ?? 0;
  const text = depth === 0 ? row.label || row.ticket_id || `#${row.id}` : `#${row.sequence}`;
  return <Box sx={{ pl: depth ? 5 : 0, minWidth: 0 }}>{text}</Box>;
}

const LEAF_COLUMNS: GridColDef<TicketRow>[] = [
  {
    field: 'inherited',
    headerName: 'Source',
    width: 170,
    sortable: false,
    renderCell: (p) =>
      p.row.inherited ? (
        <Typography variant="body2" color="text.secondary" noWrap>
          Inherited from{' '}
          {p.row.owner_booking_id ? (
            <Link
              component={RouterLink}
              to={`/bookings/${p.row.owner_booking_id}`}
              onClick={(e) => e.stopPropagation()}
              underline="hover"
            >
              #{p.row.owner_booking_id}
            </Link>
          ) : (
            'the main booking'
          )}
        </Typography>
      ) : (
        'Own'
      ),
  },
  { field: 'format_display', headerName: 'Format', width: 120 },
  { field: 'unit_display', headerName: 'Unit', width: 100 },
  { field: 'use_count', headerName: 'Uses', width: 64, align: 'right', headerAlign: 'right' },
  { field: 'redeemed_at', headerName: 'Redeemed', width: 150, renderCell: (p) => <Ts value={p.row.redeemed_at ?? null} /> },
  { field: 'noshowed_at', headerName: 'No-show', width: 150, renderCell: (p) => <Ts value={p.row.noshowed_at ?? null} /> },
  // hidden by default
  {
    field: 'value',
    headerName: 'Value',
    width: 220,
    renderCell: (p) => <Copyable value={p.row.value} label="Copy value" />,
  },
  {
    field: 'remote_value',
    headerName: 'Remote value',
    width: 180,
    renderCell: (p) => <Copyable value={p.row.remote_value} label="Copy remote value" />,
  },
  { field: 'currency', headerName: 'Currency', width: 90, renderCell: (p) => p.row.currency || '—' },
  {
    field: 'file_url',
    headerName: 'File',
    width: 80,
    sortable: false,
    renderCell: (p) =>
      p.row.file_url ? (
        <ValueActions actions={[act.copy(p.row.file_url, 'Copy file URL')]}>
          <Link href={p.row.file_url} target="_blank" rel="noopener noreferrer">
            Open
          </Link>
        </ValueActions>
      ) : (
        '—'
      ),
  },
  { field: 'created_ts', headerName: 'Created', width: 150, renderCell: (p) => <Ts value={p.row.created_ts ?? null} /> },
];

const HIDDEN_BY_DEFAULT = {
  value: false,
  remote_value: false,
  currency: false,
  file_url: false,
  created_ts: false,
};

function TicketDrawer({ ticket, onClose }: { ticket: BookingTicket | null; onClose: () => void }) {
  return (
    <Drawer anchor="right" open={ticket != null} onClose={onClose}>
      <Box sx={{ width: 420, maxWidth: '90vw', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">{ticket?.label || ticket?.ticket_id || `Ticket #${ticket?.id}`}</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {ticket && (
          <FieldGrid>
            {ticket.inherited && ticket.owner_booking_id ? (
              <LinkField
                label="Source"
                to={`/bookings/${ticket.owner_booking_id}`}
                text={`Inherited from booking #${ticket.owner_booking_id}`}
              />
            ) : (
              <Field label="Source" value={ticket.inherited ? 'Inherited from the main booking' : 'This booking'} />
            )}
            <Field
              label="Ticket ID"
              value={ticket.ticket_id ?? '—'}
              {...(ticket.ticket_id && { actions: [act.copy(ticket.ticket_id, 'Copy ticket id')] })}
            />
            <Field label="Label" value={ticket.label ?? '—'} />
            <Field label="Format" value={ticket.format_display} />
            <Field label="Unit" value={ticket.unit_display} />
            <Field label="Sequence" value={ticket.sequence} />
            <Field label="Use count" value={ticket.use_count} />
            <Field label="Redeemed at" value={<Ts value={ticket.redeemed_at} />} />
            <Field label="No-showed at" value={<Ts value={ticket.noshowed_at} />} />
            <Field
              label="Value"
              value={ticket.value ?? '—'}
              {...(ticket.value && { actions: [act.copy(ticket.value, 'Copy value')] })}
            />
            <Field
              label="Remote value"
              value={ticket.remote_value ?? '—'}
              {...(ticket.remote_value && { actions: [act.copy(ticket.remote_value, 'Copy remote value')] })}
            />
            <Field
              label="File"
              value={
                ticket.file_url ? (
                  <Link href={ticket.file_url} target="_blank" rel="noopener noreferrer">
                    Open
                  </Link>
                ) : (
                  '—'
                )
              }
              {...(ticket.file_url && { actions: [act.copy(ticket.file_url, 'Copy file URL')] })}
            />
            <Field label="Currency" value={ticket.currency ?? '—'} />
            <Field label="Net" value={formatMoney(ticket.price_net, ticket.currency, ticket.currency_precision) ?? '—'} />
            <Field label="Retail" value={formatMoney(ticket.price_retail, ticket.currency, ticket.currency_precision) ?? '—'} />
            <Field label="Created" value={<Ts value={ticket.created_ts} />} />
            <Field label="Updated" value={<Ts value={ticket.updated_ts} />} />
          </FieldGrid>
        )}
        {ticket?.data != null && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Data
            </Typography>
            <Box
              component="pre"
              sx={{ m: 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, overflow: 'auto' }}
            >
              {JSON.stringify(ticket.data, null, 2)}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export function TicketsGrid({ tickets }: { tickets: BookingTicket[] }) {
  const [selected, setSelected] = useState<BookingTicket | null>(null);
  const { rows, labelByGroup } = useMemo(() => buildRows(tickets), [tickets]);
  const ticketById = useMemo(() => new Map(tickets.map((t) => [t.id, t])), [tickets]);

  const groupingColDef: GridGroupingColDefOverride<TicketRow> = useMemo(
    () => ({
      headerName: 'Ticket',
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <GroupingCell params={p as GridRenderCellParams<TicketRow>} labelByGroup={labelByGroup} />
      ),
    }),
    [labelByGroup],
  );

  if (!tickets.length) return <EmptyLine>No tickets.</EmptyLine>;

  return (
    <>
      <DataGridPro
        treeData
        rows={rows}
        columns={LEAF_COLUMNS}
        getRowId={(r) => r.id}
        getTreeDataPath={(r) => r.path}
        getRowClassName={(p) => (p.row.inherited ? 'ticket-row--inherited' : '')}
        groupingColDef={groupingColDef}
        defaultGroupingExpansionDepth={-1}
        onRowClick={(p: GridRowParams<TicketRow>) => {
          const t = ticketById.get(p.row.id as number);
          if (t) setSelected(t);
        }}
        initialState={{ columns: { columnVisibilityModel: HIDDEN_BY_DEFAULT } }}
        sx={[
          READONLY_GRID_SX,
          {
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            // Inherited rows are context, not this booking's own data: readable, but
            // never mistaken for a ticket the booking owns.
            '& .ticket-row--inherited': { color: 'text.secondary', fontStyle: 'italic' },
          },
        ]}
        {...READONLY_GRID_PROPS}
      />
      <TicketDrawer ticket={selected} onClose={() => setSelected(null)} />
    </>
  );
}
